import { Component, OnInit, ViewChild, ChangeDetectionStrategy, ViewEncapsulation, Input, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { Theme, ThemeVisualizationEnum } from './model/theme';
import { HelperFunctions } from 'bowman-commons';

@Component({
  selector: 'app-theme-selector',
  templateUrl: './theme-selector.component.html',
  styleUrls: ['./theme-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,

})
export class ThemeSelectorComponent implements OnInit {
  @ViewChild(NgbPopover) public popover: NgbPopover;
  @ViewChild(NgbPopover) public colorPickerPopover: NgbPopover;
  @Input() inputTheme: Theme;
  @Input() disabled: boolean = false;
  @Output() inputThemeChange: EventEmitter<Theme> = new EventEmitter<Theme>();
  get shadeColor() { return ThemeSelectorComponent.shadeColor }
  get changeHue() { return ThemeSelectorComponent.changeHue }
  get generateColorPallette() { return ThemeSelectorComponent.generateColorPallette }
  public themes: Array<Theme> = [];
  public themeVisualizations = ThemeVisualizationEnum;
  public tmpTheme: Theme = new Theme('#fff');
  public tmpColors: Array<string> = [];
  public selectedColors: Array<string> = [];
  public defaultColors: { [typeName: string]: Array<string> } = {};
  public colors: Array<string>;
  constructor(private _cdr: ChangeDetectorRef) { }





  public static generateColorPallette(theme: Theme, items: number): Array<string> {
    let colors: Array<string> = [];
    if (items) {
      if (theme.visualization == ThemeVisualizationEnum.SEQUENTIAL) {
        for (let i = 1; i <= items; i++) {
          let multiplier: number = ((-(items / 2) + i) * (100 / items));

          colors.push(ThemeSelectorComponent.shadeColor(theme.color, multiplier));
        }
      } else if (theme.visualization == ThemeVisualizationEnum.DIVERGENT) {
        for (let i = 1; i <= items; i++) {
          colors.push(ThemeSelectorComponent.changeHue(theme.color, i * 50));
        }
      }

      if (colors.length == 0) {
        colors.push(theme.color);
      }
      return colors;
    }
  }

  public selectTheme(theme: Theme) {
    this.inputThemeChange.emit(theme);
    this.selectedColors = ThemeSelectorComponent.generateColorPallette(theme, 6);
    this.tmpColors = ThemeSelectorComponent.generateColorPallette(this.tmpTheme, 8);
    this._cdr.detectChanges();
  }

  public handleChangeComplete(event?: any) {
    if (event) {
      let color: string = event.color.hex;
      this.tmpTheme.color = color;
    }
    this.selectTheme(this.tmpTheme);
    this._cdr.detectChanges();
  }

  public static shadeColor(color: string, percent: number): string {
    var R: number = parseInt(color.substring(1, 3), 16);
    var G: number = parseInt(color.substring(3, 5), 16);
    var B: number = parseInt(color.substring(5, 7), 16);

    R = (R * (100 + percent)) / 100;
    G = (G * (100 + percent)) / 100;
    B = (B * (100 + percent)) / 100;

    R = R < 255 ? R : 255;
    G = G < 255 ? G : 255;
    B = B < 255 ? B : 255;

    var RR = R.toString(16).length == 1 ? "0" + R.toString(16) : R.toString(16);
    var GG = G.toString(16).length == 1 ? "0" + G.toString(16) : G.toString(16);
    var BB = B.toString(16).length == 1 ? "0" + B.toString(16) : B.toString(16);

    return "#" + RR.split(".")[0] + GG.split(".")[0] + BB.split(".")[0];
  }

  public static changeColorOpacity(color: string, percent: number): string {
    var R: number = parseInt(color.substring(1, 3), 16);
    var G: number = parseInt(color.substring(3, 5), 16);
    var B: number = parseInt(color.substring(5, 7), 16);


    return "rgba(" + R + ", " + G + ", " + B + ", " + percent + ")";
  }


  public static changeHue(rgb, degree) {
    var hsl = ThemeSelectorComponent.rgbToHSL(rgb);
    hsl.h += degree;
    if (hsl.h > 360) {
      hsl.h -= 360;
    }
    else if (hsl.h < 0) {
      hsl.h += 360;
    }
    return ThemeSelectorComponent.hslToRGB(hsl);
  }

  // exepcts a string and returns an object
  public static rgbToHSL(rgb) {
    // strip the leading # if it's there
    rgb = rgb.replace(/^\s*#|\s*$/g, '');

    // convert 3 char codes --> 6, e.g. `E0F` --> `EE00FF`
    if (rgb.length == 3) {
      rgb = rgb.replace(/(.)/g, '$1$1');
    }

    var r = parseInt(rgb.substr(0, 2), 16) / 255,
      g = parseInt(rgb.substr(2, 2), 16) / 255,
      b = parseInt(rgb.substr(4, 2), 16) / 255,
      cMax = Math.max(r, g, b),
      cMin = Math.min(r, g, b),
      delta = cMax - cMin,
      l = (cMax + cMin) / 2,
      h = 0,
      s = 0;

    if (delta == 0) {
      h = 0;
    }
    else if (cMax == r) {
      h = 60 * (((g - b) / delta) % 6);
    }
    else if (cMax == g) {
      h = 60 * (((b - r) / delta) + 2);
    }
    else {
      h = 60 * (((r - g) / delta) + 4);
    }

    if (delta == 0) {
      s = 0;
    }
    else {
      s = (delta / (1 - Math.abs(2 * l - 1)))
    }

    return {
      h: h,
      s: s,
      l: l
    }
  }

  // expects an object and returns a string
  public static hslToRGB(hsl) {
    var h = hsl.h,
      s = hsl.s,
      l = hsl.l,
      c = (1 - Math.abs(2 * l - 1)) * s,
      x = c * (1 - Math.abs((h / 60) % 2 - 1)),
      m = l - c / 2,
      r, g, b;

    if (h < 60) {
      r = c;
      g = x;
      b = 0;
    }
    else if (h < 120) {
      r = x;
      g = c;
      b = 0;
    }
    else if (h < 180) {
      r = 0;
      g = c;
      b = x;
    }
    else if (h < 240) {
      r = 0;
      g = x;
      b = c;
    }
    else if (h < 300) {
      r = x;
      g = 0;
      b = c;
    }
    else {
      r = c;
      g = 0;
      b = x;
    }

    r = ThemeSelectorComponent.normalize_rgb_value(r, m);
    g = ThemeSelectorComponent.normalize_rgb_value(g, m);
    b = ThemeSelectorComponent.normalize_rgb_value(b, m);

    return ThemeSelectorComponent.rgbToHex(r, g, b);
  }

  public static normalize_rgb_value(color, m) {
    color = Math.floor((color + m) * 255);
    if (color < 0) {
      color = 0;
    }
    return color;
  }

  public static rgbToHex(r, g, b) {
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  }


  ngOnInit() {
    this.tmpTheme = HelperFunctions.cloneObject(this.inputTheme);
    this.colors = ['#B80000', '#DB3E00', '#FCCB00', '#008B02', '#006B76', '#1273DE', '#004DCF', '#5300EB', '#EB9694', '#FAD0C3', '#FEF3BD', '#C1E1C5', '#BEDADC', '#C4DEF6', '#BED3F3', '#D4C4FB'];
    this.themes = [new Theme('#6c757d'), new Theme('#D6873D'), new Theme('#55779E'), new Theme('#77A778'), new Theme('#EB9694', ThemeVisualizationEnum.DIVERGENT)]
    this.themes.forEach(theme => {
      this.defaultColors[theme.color] = ThemeSelectorComponent.generateColorPallette(theme, 8);
    })
    if (this.inputTheme && this.inputTheme.color != null) {
      this.selectedColors = ThemeSelectorComponent.generateColorPallette(this.inputTheme, 6);
    }
    this.tmpColors = ThemeSelectorComponent.generateColorPallette(this.tmpTheme, 8);
  }

}
